// Хранилище бинарных blob'ов поверх Cache API.
//
// Общая основа для image/audio-кэшей. Раньше оба адаптера были заглушками,
// бросавшими исключение во ВСЕХ методах: любое обращение к порту падало, из-за
// чего офлайн-кэш медиа на web фактически отсутствовал.
//
// Cache API, а не IndexedDB: он хранит Response целиком (включая content-type),
// переживает перезагрузку и разделяет квоту origin с Service Worker'ом.

import type { AudioCacheEntryMeta } from "../../ports/audio-cache.port";

const KEY_ORIGIN = "https://studyhub.cache";

/** Момент записи — для LRU. Проставляется в `put`, читается в `meta`. */
const HEADER_CACHED_AT = "x-cached-at";
/** Размер тела — чтобы `meta`/`sizeBytes` не читали blob целиком. */
const HEADER_BYTES = "x-cache-bytes";

function requestFor(namespace: string, key: string): Request {
	return new Request(`${KEY_ORIGIN}/${namespace}/${encodeURIComponent(key)}`);
}

/** Обратное преобразование URL записи → исходный ключ. */
function keyFromUrl(url: string, namespace: string): string | null {
	const marker = `${KEY_ORIGIN}/${namespace}/`;
	if (!url.startsWith(marker)) return null;
	try {
		return decodeURIComponent(url.slice(marker.length));
	} catch {
		return null;
	}
}

function metaFromResponse(response: Response): Promise<AudioCacheEntryMeta> {
	const cachedAt = Number(response.headers.get(HEADER_CACHED_AT));
	const declaredBytes = Number(response.headers.get(HEADER_BYTES));
	const base = { cachedAt: Number.isFinite(cachedAt) && cachedAt > 0 ? cachedAt : 0 };
	if (Number.isFinite(declaredBytes) && declaredBytes > 0) {
		return Promise.resolve({ ...base, bytes: declaredBytes });
	}
	// Записи, положенные до появления заголовка, — считаем размер по телу.
	return response
		.blob()
		.then((blob) => ({ ...base, bytes: blob.size }))
		.catch(() => ({ ...base, bytes: 0 }));
}

export function createBlobCache(cacheName: string, namespace: string) {
	const available = typeof caches !== "undefined";

	async function metaOf(key: string): Promise<AudioCacheEntryMeta | null> {
		if (!available) return null;
		try {
			const cache = await caches.open(cacheName);
			const hit = await cache.match(requestFor(namespace, key));
			return hit ? await metaFromResponse(hit) : null;
		} catch {
			return null;
		}
	}

	async function listKeys(): Promise<string[]> {
		if (!available) return [];
		try {
			const cache = await caches.open(cacheName);
			const requests = await cache.keys();
			const result: string[] = [];
			for (const request of requests) {
				const key = keyFromUrl(request.url, namespace);
				if (key !== null) result.push(key);
			}
			return result;
		} catch {
			return [];
		}
	}

	return {
		async get(key: string): Promise<Blob | null> {
			if (!available) return null;
			try {
				const cache = await caches.open(cacheName);
				const hit = await cache.match(requestFor(namespace, key));
				return hit ? await hit.blob() : null;
			} catch {
				return null;
			}
		},

		async put(key: string, blob: Blob): Promise<void> {
			if (!available) return;
			try {
				const cache = await caches.open(cacheName);
				await cache.put(
					requestFor(namespace, key),
					new Response(blob, {
						headers: {
							"content-type": blob.type || "application/octet-stream",
							[HEADER_CACHED_AT]: String(Date.now()),
							[HEADER_BYTES]: String(blob.size),
						},
					}),
				);
			} catch {
				// Переполнена квота / приватный режим — кэш опционален.
			}
		},

		async evict(key: string): Promise<void> {
			if (!available) return;
			try {
				const cache = await caches.open(cacheName);
				await cache.delete(requestFor(namespace, key));
			} catch {
				// no-op
			}
		},

		async size(): Promise<number> {
			return (await listKeys()).length;
		},

		keys: listKeys,

		meta: metaOf,

		async sizeBytes(): Promise<number> {
			const keys = await listKeys();
			const metas = await Promise.all(keys.map((key) => metaOf(key)));
			return metas.reduce((sum, entry) => sum + (entry?.bytes ?? 0), 0);
		},
	};
}
